import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Edit, Trash2, ArrowLeft, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminProducts = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        price: '',
        type: 'sample_pack',
        image_url: '',
        audio_url: '',
        featured: false
    });
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!user?.is_admin) {
            toast.error('Admin access required');
            navigate('/');
            return;
        }
        fetchProducts();
    }, [user, navigate]);

    const fetchProducts = async () => {
        try {
            const response = await axios.get(`${API}/products`);
            setProducts(response.data);
        } catch (error) {
            console.error('Error fetching products:', error);
            toast.error('Failed to load products');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const productData = {
                ...formData,
                price: parseFloat(formData.price)
            };

            if (editingProduct) {
                await axios.put(`${API}/admin/products/${editingProduct.id}`, productData);
                toast.success('Product updated successfully!');
            } else {
                await axios.post(`${API}/admin/products`, productData);
                toast.success('Product created successfully!');
            }

            setShowForm(false);
            setEditingProduct(null);
            resetForm();
            fetchProducts();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save product');
        }
    };

    const handleEdit = (product) => {
        setEditingProduct(product);
        setFormData({
            name: product.name,
            description: product.description,
            price: product.price.toString(),
            type: product.type,
            image_url: product.image_url,
            audio_url: product.audio_url,
            featured: product.featured
        });
        setShowForm(true);
    };

    const handleDelete = async (productId) => {
        if (!window.confirm('Are you sure you want to delete this product?')) return;

        try {
            await axios.delete(`${API}/admin/products/${productId}`);
            toast.success('Product deleted successfully!');
            fetchProducts();
        } catch (error) {
            toast.error('Failed to delete product');
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            price: '',
            type: 'sample_pack',
            image_url: '',
            audio_url: '',
            featured: false
        });
    };

    const getTypeLabel = (type) => {
        switch (type) {
            case 'sample_pack':
                return 'Sample Pack';
            case 'song':
                return 'Song';
            case 'album':
                return 'Album';
            default:
                return type;
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center pt-16">
                <div className="text-xl text-muted-foreground">Loading...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen pt-24 px-6 md:px-12 lg:px-24 py-12" data-testid="admin-products">
            <button 
                onClick={() => navigate('/admin')}
                className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors mb-8"
                data-testid="back-to-dashboard"
            >
                <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
                <span className="text-sm uppercase tracking-widest">Back to Dashboard</span>
            </button>

            <div className="flex items-center justify-between mb-12">
                <div>
                    <h1 className="text-5xl font-bold tracking-tighter mb-2">Manage Products</h1>
                    <p className="text-muted-foreground">{products.length} total products</p>
                </div>
                <button 
                    onClick={() => {
                        setShowForm(true);
                        setEditingProduct(null);
                        resetForm();
                    }}
                    className="btn-primary flex items-center gap-2"
                    data-testid="add-product-button"
                >
                    <Plus className="w-5 h-5" />
                    Add Product
                </button>
            </div>

            {/* Product Form Modal */}
            {showForm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
                    <div className="bg-black border border-white/10 w-full max-w-2xl p-8 relative max-h-[90vh] overflow-y-auto">
                        <button 
                            onClick={() => {
                                setShowForm(false);
                                setEditingProduct(null);
                            }}
                            className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-full transition-colors"
                            data-testid="close-form"
                        >
                            <X className="w-5 h-5" />
                        </button>

                        <h2 className="text-3xl font-bold mb-6 tracking-tight">
                            {editingProduct ? 'Edit Product' : 'Add New Product'}
                        </h2>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm uppercase tracking-widest text-muted-foreground mb-2">
                                    Product Name *
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full bg-black border-white/20 border focus:border-primary rounded-none h-12 px-4 text-sm"
                                    required
                                    data-testid="product-name-input"
                                />
                            </div>

                            <div>
                                <label className="block text-sm uppercase tracking-widest text-muted-foreground mb-2">
                                    Description *
                                </label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full bg-black border-white/20 border focus:border-primary rounded-none h-24 px-4 py-3 text-sm resize-none"
                                    required
                                    data-testid="product-description-input"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm uppercase tracking-widest text-muted-foreground mb-2">
                                        Price (USD) *
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={formData.price}
                                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                        className="w-full bg-black border-white/20 border focus:border-primary rounded-none h-12 px-4 text-sm"
                                        required
                                        data-testid="product-price-input"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm uppercase tracking-widest text-muted-foreground mb-2">
                                        Type *
                                    </label>
                                    <select
                                        value={formData.type}
                                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                        className="w-full bg-black border-white/20 border focus:border-primary rounded-none h-12 px-4 text-sm"
                                        required
                                        data-testid="product-type-select"
                                    >
                                        <option value="sample_pack">Sample Pack</option>
                                        <option value="song">Song</option>
                                        <option value="album">Album</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm uppercase tracking-widest text-muted-foreground mb-2">
                                    Image URL *
                                </label>
                                <input
                                    type="url"
                                    value={formData.image_url}
                                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                                    className="w-full bg-black border-white/20 border focus:border-primary rounded-none h-12 px-4 text-sm"
                                    placeholder="https://..."
                                    required
                                    data-testid="product-image-input"
                                />
                            </div>

                            <div>
                                <label className="block text-sm uppercase tracking-widest text-muted-foreground mb-2">
                                    Audio URL *
                                </label>
                                <input
                                    type="url"
                                    value={formData.audio_url}
                                    onChange={(e) => setFormData({ ...formData, audio_url: e.target.value })}
                                    className="w-full bg-black border-white/20 border focus:border-primary rounded-none h-12 px-4 text-sm"
                                    placeholder="https://..."
                                    required
                                    data-testid="product-audio-input"
                                />
                            </div>

                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    id="featured"
                                    checked={formData.featured}
                                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                                    className="w-5 h-5"
                                    data-testid="product-featured-checkbox"
                                />
                                <label htmlFor="featured" className="text-sm">
                                    Mark as Featured Product
                                </label>
                            </div>

                            <div className="flex gap-3 pt-4">
                                <button 
                                    type="submit"
                                    className="btn-primary flex-1"
                                    data-testid="save-product-button"
                                >
                                    {editingProduct ? 'Update Product' : 'Create Product'}
                                </button>
                                <button 
                                    type="button"
                                    onClick={() => {
                                        setShowForm(false);
                                        setEditingProduct(null);
                                    }}
                                    className="btn-secondary"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Products Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map((product) => (
                    <div 
                        key={product.id}
                        className="bg-card border border-border overflow-hidden"
                        data-testid={`product-item-${product.id}`}
                    >
                        <div className="aspect-square overflow-hidden">
                            <img 
                                src={product.image_url} 
                                alt={product.name}
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="p-4">
                            <div className="flex items-start justify-between mb-2">
                                <div className="flex-1">
                                    <h3 className="font-semibold text-lg mb-1">{product.name}</h3>
                                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
                                        {getTypeLabel(product.type)}
                                        {product.featured && ' • Featured'}
                                    </p>
                                </div>
                            </div>
                            <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                                {product.description}
                            </p>
                            <div className="flex items-center justify-between">
                                <span className="text-xl font-bold text-primary">${product.price}</span>
                                <div className="flex gap-2">
                                    <button 
                                        onClick={() => handleEdit(product)}
                                        className="p-2 hover:bg-white/10 rounded transition-colors"
                                        data-testid={`edit-product-${product.id}`}
                                    >
                                        <Edit className="w-4 h-4" />
                                    </button>
                                    <button 
                                        onClick={() => handleDelete(product.id)}
                                        className="p-2 hover:bg-destructive/10 text-destructive rounded transition-colors"
                                        data-testid={`delete-product-${product.id}`}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {products.length === 0 && (
                <div className="text-center py-24">
                    <p className="text-xl text-muted-foreground mb-6">No products yet</p>
                    <button 
                        onClick={() => setShowForm(true)}
                        className="btn-primary"
                    >
                        Add Your First Product
                    </button>
                </div>
            )}
        </div>
    );
};

export default AdminProducts;
